import { Dispatch } from "@reduxjs/toolkit";
import {
  setLoading,
  setError,
  setShiftHandoverQuestions,
} from "../../store/slices/shiftHandOverSlice";
import { createApiClient, apiClient } from "../../api/apiClient";
import store from "../../store";
import { handleShiftHandoverErrorWithFriendlyMessage, SHIFTHANDOVER_API_NAMES } from "../../store/utils/shiftHandoverErrorHandler";

interface FetchQuestionsParams {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setApiError: React.Dispatch<React.SetStateAction<string | null>>;
  dispatch: Dispatch;
  handoverState?: 'new' | 'in-progress' | 'completed'; // Current handover state
  walkId?: number | null; // Walk ID for in-progress handovers
}

interface Question {
  id: string;
  questionText: string;
  category: string;
  details?: string; // Added details field
  material?: string; // Added material field
  Pallet_Type?: string; // Added Pallet_Type field
}

interface ApiQuestion {
  ID: number;
  Question: string;
  Details: string | null;
  Observation: string;
  SPARC_Category: string;
  Current_Category: string;
  Building_Status: string;
  Material: string | null;
  Pallet_Type: string | null;
  Column_Tag: string | null;
  Building_Tag: string | null;
  Column_30: string | null;
  Compliance_Asset_Id: string | null;
  OBSRVTN_TYPE_NAME: string;
  ROLE_NAME: string;
}

interface ApiResponse {
  message: string;
  data: {
    [category: string]: ApiQuestion[];
  };
}

/**
 * Fetches shift handover questions from the API if they haven't been loaded already
 * @returns Promise that resolves when questions are loaded or rejects on error
 */
export const fetchShiftHandoverQuestions = async ({
  setIsLoading,
  setApiError,
  dispatch,
  handoverState = 'new',
  walkId = null,
}: FetchQuestionsParams): Promise<boolean> => {
  try {
    // Get current state to check if questions are already loaded
    const state = store.getState();
    if (
      state.shiftHandOver.questionsLoaded &&
      state.shiftHandOver.shiftHandoverQuestions.length > 0
    ) {
      // Questions already loaded, no need to fetch again
      console.log("Questions already loaded, skipping fetch");
      return true;
    }

    // Set loading state
    setIsLoading(true);
    dispatch(setLoading(true));

    // Clear previous errors
    setApiError(null);
    dispatch(setError(null));

    // Create API client
    const api = createApiClient();

    console.log("Fetching shift handover questions from API");
    console.log('[fetchShiftHandoverQuestions] handoverState:', handoverState, 'walkId:', walkId);

    // Build URL: append recordId only for in-progress handovers
    const recordIdParam = handoverState === 'in-progress' && walkId ? `?recordId=${walkId}` : '';
    const questionsUrl = `/shift-handover-walks/questions${recordIdParam}`;

    // Make API call to fetch questions
    // completed  -> skip org context (preserve original question set)
    // in-progress -> include org context + recordId
    // new        -> include org context only
    const requestConfig = handoverState === 'completed'
      ? { _skipOrgContextForQuestions: true }
      : undefined;

    const response = await apiClient(
      api,
      questionsUrl,
      "GET",
      undefined,
      requestConfig
    );

    // Transform API response to match our state structure
    if (response && response.data) {
      const { data } = response;
      const transformedQuestions: Question[] = [];

      // Process each category and its questions
      Object.entries(data).forEach(([category, questions]) => {
        (questions as ApiQuestion[]).forEach((question: ApiQuestion) => {
          transformedQuestions.push({
            id: question.ID.toString(),
            questionText: question.Observation,
            category,
            details: question.Details || undefined, // Add Details field
            material: question.Material || undefined, // Add Material field
            Pallet_Type: question.Pallet_Type || undefined, // Add Pallet_Type field from API
          });
        });
      });

      console.log(
        `Processed ${transformedQuestions.length} questions from API`
      );

      // Dispatch the transformed questions to Redux
      dispatch(setShiftHandoverQuestions(transformedQuestions));
    } else {
      // API returned success but no valid data - treat as error
      console.error("API returned success but no valid data");
      const noDataError = new Error("No questions data returned from API");
      
      handleShiftHandoverErrorWithFriendlyMessage(
        SHIFTHANDOVER_API_NAMES.FETCH_HANDOVER_QUESTIONS,
        noDataError,
        true, // Show modal overlay
        {
          reason: "Empty or invalid response data"
        }
      );
    }

    setIsLoading(false);
    dispatch(setLoading(false));
    return true;
  } catch (error) {
    setIsLoading(false);
    dispatch(setLoading(false));

    const errorMessage =
      error instanceof Error
        ? error.message
        : "An error occurred while fetching shift handover questions";

    // Show error overlay modal
    handleShiftHandoverErrorWithFriendlyMessage(
      SHIFTHANDOVER_API_NAMES.FETCH_HANDOVER_QUESTIONS,
      error,
      true, // Show modal overlay
      {}
    );

    setApiError(errorMessage);
    dispatch(setError(errorMessage));
    console.error("Error fetching shift handover questions:", error);
    return false;
  }
};
